import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../services/apiService';
import { ENDPOINTS } from '../config/api';
import StatusPill from '../components/ui/StatusPill';

const n = v => Number(v) || 0;
const fmtMoney = (v) => { try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n(v)); } catch { return `$${n(v).toLocaleString()}`; } };
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';
const toArr = x => Array.isArray(x) ? x : (x?.items || []);

const Stat = ({ label, value, sub, tone, first }) => (
  <div style={{ paddingRight: 26, paddingLeft: first ? 0 : 26, borderLeft: first ? 'none' : '1px solid #26262B' }}>
    <div className="field-label" style={{ marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 700, color: tone || '#FAFAFA', fontFamily: '"JetBrains Mono", monospace', lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#A1A1AA', marginTop: 5 }}>{sub}</div>}
  </div>
);

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, o] = await Promise.all([
      apiGet(ENDPOINTS.ADMIN.STATS).catch(() => null),
      apiGet(ENDPOINTS.ADMIN.ORGANIZATIONS, { limit: 8, sort: 'created_at', order: 'desc' }).catch(() => []),
    ]);
    setStats(s);
    setOrgs(toArr(o));
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const totalOrgs   = stats?.total_organizations ?? orgs.length;
  const activeOrgs  = stats?.active_organizations ?? orgs.filter(o => (o.status || 'active') === 'active').length;
  const totalUsers  = stats?.total_users ?? '-';
  const mrr         = stats?.mrr ?? stats?.monthly_revenue;

  return (
    <div className="page">
      {/* Head */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#A1A1AA', marginBottom: 4 }}>Console / <strong style={{ color: '#FAFAFA' }}>Overview</strong></div>
        <h1 style={{ fontFamily: '"Geist","Inter",system-ui,sans-serif', fontWeight: 600, fontSize: 44, letterSpacing: '-0.02em', color: '#FAFAFA', margin: 0, lineHeight: 1.05 }}>
          Platform <em style={{ fontStyle: 'italic', background: 'linear-gradient(135deg,#60A5FA,#3B82F6 55%,#2563EB)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>overview</em>
        </h1>
        <p style={{ color: '#A1A1AA', fontSize: 14, margin: '4px 0 0' }}>All organizations on NexCarat at a glance.</p>
      </div>

      {/* Stat strip */}
      <div className="flex flex-wrap items-start" style={{ gap: 0, rowGap: 18, paddingBottom: 20, borderBottom: '1px solid #26262B', marginBottom: 22 }}>
        <Stat first label="Organizations" value={totalOrgs} sub={`${activeOrgs} active`} />
        <Stat label="Total users" value={totalUsers} />
        <Stat label="MRR" value={mrr != null ? fmtMoney(mrr) : '-'} tone="#22C55E" />
        <Stat label="Trialing" value={stats?.trialing_organizations ?? '-'} tone="#F59E0B" />
      </div>

      {/* Recent organizations */}
      <div className="flex items-center justify-between gap-4" style={{ borderBottom: '1px solid #26262B', paddingBottom: 12, marginBottom: 14 }}>
        <div className="card-title">Recent organizations</div>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/organizations')}>View all →</button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="tbl" style={{ width: '100%' }}>
          <thead>
            <tr><th>Organization</th><th>Plan</th><th>Users</th><th>Status</th><th>Joined</th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#A1A1AA' }}>Loading…</td></tr>}
            {!loading && orgs.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#71717A' }}>No organizations yet</td></tr>}
            {orgs.map(o => (
              <tr key={o.id} className="cursor-pointer" onClick={() => navigate(`/organizations/${o.id}`)}>
                <td><span className="tbl-id">{o.name}</span></td>
                <td style={{ color: '#D4D4D8' }}>{o.plan || o.plan_name || '-'}</td>
                <td className="tbl-mono">{o.user_count ?? '-'}</td>
                <td><StatusPill status={(o.status || 'active') === 'active' ? 'active' : 'on_hold'} label={(o.status || 'active') === 'active' ? 'Active' : (o.status || 'Inactive')} /></td>
                <td className="tbl-mono">{fmtDate(o.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
